import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";
import Reveal, { stagger, fadeUp, EASE } from "@/components/motion/Reveal";

export default function Roadmap() {
  const { t, dir } = useI18n();
  const r = t.roadmap;
  return (
    <section id="roadmap" data-testid="roadmap-section" className="section-pad bg-surface-dark text-white relative overflow-hidden">
      <div className="absolute inset-0 dot-bg opacity-40" aria-hidden />
      <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
      <div className="absolute -bottom-40 start-[-10%] h-[480px] w-[480px] rounded-full bg-brand-red/10 blur-3xl" aria-hidden />
      <div className="container-pro relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <Reveal>
            <div className="overline">{r.kicker}</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white">
              {r.title1}
              <br />
              <span className="text-brand-red">{r.title2}</span>
            </h2>
          </Reveal>
          <Reveal variants={fadeUp}>
            <p className="max-w-md text-white/60">{r.subtitle}</p>
          </Reveal>
        </div>

        {/* Desktop timeline */}
        <div className="hidden md:block">
          <div className="relative">
            <motion.div
              className="absolute top-[34px] left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-red to-transparent"
              style={{ originX: dir === "rtl" ? 1 : 0 }}
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.6, ease: EASE }}
              aria-hidden
            />
            <Reveal variants={stagger(0.15, 0.14)} className="grid grid-cols-5 gap-4">
              {r.phases.map((m, i) => (
                <Reveal key={m.year} variants={fadeUp} data-testid="roadmap-item" className="relative">
                  <div className="flex flex-col items-start">
                    <div className="font-mono text-xs uppercase tracking-widest text-white/40">{r.phaseLabel} {i + 1}</div>
                    <div className="relative mt-3 h-[14px] w-[14px] rounded-full bg-brand-red ring-4 ring-brand-red/20" />
                  </div>
                  <div className="mt-6">
                    <div className="font-mono text-sm text-brand-teal">{m.year}</div>
                    <h3 className="mt-1 font-heading text-lg font-semibold text-white">{m.title}</h3>
                    <ul className="mt-3 space-y-1.5">
                      {m.items.map((it, j) => (
                        <li key={j} className="text-sm text-white/70 leading-relaxed">— {it}</li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </Reveal>
          </div>
        </div>

        {/* Mobile stacked timeline */}
        <div className="md:hidden space-y-8">
          {r.phases.map((m, i) => (
            <Reveal key={m.year} variants={fadeUp} className="flex gap-4" data-testid="roadmap-item-mobile">
              <div className="flex flex-col items-center">
                <div className="h-3 w-3 rounded-full bg-brand-red ring-4 ring-brand-red/20" />
                <div className="w-px flex-1 bg-white/20 mt-2" />
              </div>
              <div className="pb-2">
                <div className="font-mono text-xs uppercase tracking-widest text-white/40">{r.phaseLabel} {i + 1}</div>
                <div className="font-mono text-sm text-brand-teal mt-1">{m.year}</div>
                <h3 className="mt-1 font-heading text-lg font-semibold text-white">{m.title}</h3>
                <ul className="mt-2 space-y-1">
                  {m.items.map((it, j) => (
                    <li key={j} className="text-sm text-white/70">— {it}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
